import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { Badge } from "../components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { MapPin, Calendar, Globe, Building2, BadgeCheck, Loader2, Star } from "lucide-react";
import { toast } from "sonner";
import RatingStars from "../components/RatingStars";

const ROLE_LABELS = {
  admin: "Administrateur",
  organizer: "Organisateur",
  exhibitor: "Exposant",
  venue_owner: "Propriétaire de lieu",
  provider: "Prestataire",
  visitor: "Visiteur",
};

export default function UserProfile() {
  const { id } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/users/${id}/public`)
      .then((r) => setProfile(r.data))
      .catch(() => toast.error("Profil introuvable"))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="max-w-5xl mx-auto px-4 py-16"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (!profile) return null;

  const joinedYear = profile.created_at ? new Date(profile.created_at).getFullYear() : null;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Hero */}
      <div className="bg-white border border-ink/10 rounded-md p-8 lg:p-10 mb-10">
        <div className="flex flex-col sm:flex-row gap-6">
          <Avatar className="h-24 w-24 ring-4 ring-cream-100">
            <AvatarImage src={profile.picture || ""} />
            <AvatarFallback className="bg-ink text-cream text-2xl font-display">
              {profile.name?.[0]?.toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <Badge variant="outline" className="uppercase tracking-wider text-[10px]">
                {ROLE_LABELS[profile.role] || profile.role}
              </Badge>
              {profile.verified && (
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-trust">
                  <BadgeCheck className="h-3.5 w-3.5" /> Vérifié
                </span>
              )}
            </div>
            <h1 data-testid="profile-name" className="font-heading font-light tracking-editorial text-3xl lg:text-4xl text-ink mb-2">
              {profile.name}
            </h1>
            {profile.company && (
              <p className="text-sm text-muted-foreground flex items-center gap-1.5 mb-1">
                <Building2 className="h-3.5 w-3.5" /> {profile.company}
              </p>
            )}
            {profile.website && (
              <a
                href={profile.website}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-accent hover:underline flex items-center gap-1.5 mb-3"
                data-testid="profile-website"
              >
                <Globe className="h-3.5 w-3.5" /> {profile.website.replace(/^https?:\/\//, "")}
              </a>
            )}
            {profile.bio && (
              <p className="text-ink/75 leading-relaxed mt-3 max-w-2xl">{profile.bio}</p>
            )}
            <div className="flex items-center gap-4 mt-4 text-xs text-muted-foreground">
              {joinedYear && <span>Membre depuis {joinedYear}</span>}
              {profile.rating_avg > 0 && (
                <span className="flex items-center gap-1.5">
                  <RatingStars rating={profile.rating_avg} count={profile.rating_count} size={12} />
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Public listings */}
      {profile.events?.length > 0 && (
        <section className="mb-10">
          <h2 className="font-heading font-medium text-2xl text-ink mb-5 tracking-tight">
            Événements <span className="text-ink/40 text-base font-normal">({profile.events.length})</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {profile.events.map((e) => (
              <Link
                key={e.id}
                to={`/events/${e.id}`}
                data-testid={`profile-event-${e.id}`}
                className="bg-white border border-ink/10 rounded-md p-4 hover:border-ink/30 transition"
              >
                <Badge variant="outline" className="mb-2 text-[10px] uppercase">{e.category}</Badge>
                <p className="font-heading font-medium text-ink">{e.title}</p>
                <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {new Date(e.start_date).toLocaleDateString("fr-FR")}</span>
                  <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {e.city}</span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {profile.venues?.length > 0 && (
        <section className="mb-10">
          <h2 className="font-heading font-medium text-2xl text-ink mb-5 tracking-tight">
            Lieux <span className="text-ink/40 text-base font-normal">({profile.venues.length})</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {profile.venues.map((v) => (
              <Link
                key={v.id}
                to={`/venues/${v.id}`}
                data-testid={`profile-venue-${v.id}`}
                className="bg-white border border-ink/10 rounded-md p-4 hover:border-ink/30 transition"
              >
                <p className="font-heading font-medium text-ink">{v.name}</p>
                <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                  <MapPin className="h-3 w-3" /> {v.city}
                </p>
                <p className="text-sm font-bold text-ink mt-2">{v.price_per_day}€<span className="text-xs text-ink/40"> / jour</span></p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {profile.providers?.length > 0 && (
        <section className="mb-10">
          <h2 className="font-heading font-medium text-2xl text-ink mb-5 tracking-tight">
            Prestations <span className="text-ink/40 text-base font-normal">({profile.providers.length})</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {profile.providers.map((p) => (
              <Link
                key={p.id}
                to={`/providers/${p.id}`}
                data-testid={`profile-provider-${p.id}`}
                className="bg-white border border-ink/10 rounded-md p-4 hover:border-ink/30 transition"
              >
                <Badge variant="outline" className="mb-2 text-[10px] uppercase">{p.category}</Badge>
                <p className="font-heading font-medium text-ink">{p.name}</p>
                <p className="text-xs text-muted-foreground mt-1">{p.zone}</p>
                <p className="text-sm font-bold text-ink mt-2">{p.price_min}€ – {p.price_max}€</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Reviews received */}
      {profile.reviews_received?.length > 0 && (
        <section>
          <h2 className="font-heading font-medium text-2xl text-ink mb-5 tracking-tight">
            Avis reçus <span className="text-ink/40 text-base font-normal">({profile.reviews_received.length})</span>
          </h2>
          <div className="space-y-3">
            {profile.reviews_received.slice(0, 10).map((r) => (
              <div key={r.id} className="bg-white border border-ink/10 rounded-md p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-semibold text-ink">{r.user_name}</p>
                  <div className="flex items-center gap-1 text-accent">
                    {[1,2,3,4,5].map(i => (
                      <Star key={i} className={`h-3 w-3 ${i <= r.rating ? "fill-accent" : "text-ink/20"}`} />
                    ))}
                  </div>
                </div>
                {r.comment && <p className="text-sm text-ink/70 italic">"{r.comment}"</p>}
              </div>
            ))}
          </div>
        </section>
      )}

      {profile.events?.length === 0 && profile.venues?.length === 0 && profile.providers?.length === 0 && (
        <div className="bg-cream-50 border border-ink/10 rounded-md p-12 text-center">
          <p className="text-muted-foreground">Aucune publication publique pour l'instant.</p>
        </div>
      )}
    </div>
  );
}
